import { Box, Grid } from "@mui/material";
import { format } from "date-fns";
import { Auth0UserSerializer } from "recoil/types";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { printPdfFromBlob } from "../../utils/printUtils";
import { Button } from "../shared/Button/Button";
import {
  BoxConfirmation,
  ConfirmationWrapper,
  NewReqConfirmationButtonWrapper,
  FormLabelConfirmation,
  NewReqBillingConfirmationList,
  NewReqConfirmationMessageText,
  NewRequisitionTextField,
  NewReqConfirmationMessage,
} from "./NewRequisition.styles";
import { GetTypographyContent } from "../shared/Typography/Typography";
import { NewRequisitionType } from "./types";
import { getOnlineOrderPrintout } from "./Services/NewReqService";
import { ConfirmationGraphic } from "./ConfirmationGraphic";
import { PrintIcon } from "../../assets/iconComponents/PrintIcon";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { fetchOrLogout } from "../../utils/helpers";
import { SendActionType } from "../shared/Services/SharedServices";
import { getBillToClientText } from "./helpers";

const getBillToPayment = (
  insurance: boolean,
  patient: boolean,
  client: boolean,
  auth: Auth0UserSerializer | undefined,
  pendingMedicaid?: boolean,
  cashPay?: boolean,
) => {
  if (insurance) {
    return "Bill To Insurance";
  }
  if (patient) {
    return "Bill To Patient";
  }
  if (client) {
    return getBillToClientText(auth);
  }
  if (pendingMedicaid) {
    return "Pending Medicaid";
  }
  if (cashPay) {
    return "Cash Pay";
  }
};

const Confirmation: React.FC<{
  handleClose: () => void;
  submittedRequisition: NewRequisitionType;
}> = ({ handleClose, submittedRequisition }) => {
  const { auth, setLoggedOut } = useAuthenticationHook();
  const { physicalDeviceType } = useDeviceHook();

  const handlePrint = async () => {
    if (auth) {
      const pdfUrl = await getOnlineOrderPrintout(
        auth.token,
        submittedRequisition.unity_complete_id,
        setLoggedOut,
      );
      if (pdfUrl) {
        await SendActionType(
          "ProviderOnlineOrderPrintModal",
          setLoggedOut,
          auth?.token,
        );
        const response = await fetch(pdfUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/pdf",
          },
        });
        const blob = await response.blob();
        const filename = `${submittedRequisition.first_name?.slice(0, 1) || "P"}${submittedRequisition.last_name?.slice(0, 1) || "P"}_ordered_report.pdf`;

        await printPdfFromBlob(blob, filename, physicalDeviceType);
      }
    }
  };

  return (
    <ConfirmationWrapper>
      <Grid container spacing={3}>
        <Grid item xs={12} style={{ marginTop: "20px" }}>
          <FormLabelConfirmation sx={{ fontFamily: "light" }}>
            {" "}
            {GetTypographyContent({
              content:
                "Your order has been confirmed and will be processed soon.",
              size: "large",
              isBold: true,
              textColor: "black",
            })}
          </FormLabelConfirmation>
        </Grid>

        <Grid item xs={12} sm={4} md={4}>
          <NewRequisitionTextField
            fullWidth
            focused
            disabled
            value={format(new Date(), "MM/dd/yyyy")}
            label="Order Date"
            variant="standard"
          />
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <NewRequisitionTextField
            fullWidth
            focused
            disabled
            value={submittedRequisition.unity_complete_id}
            label="Order Number"
            variant="standard"
          />
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <NewRequisitionTextField
            fullWidth
            focused
            disabled
            value={getBillToPayment(
              submittedRequisition.is_bill_to_insurance,
              submittedRequisition.is_bill_to_patient,
              submittedRequisition.is_bill_to_client,
              auth,
              submittedRequisition.is_pending_medicaid,
              submittedRequisition.is_cash_pay,
            )}
            label="Payment"
            variant="standard"
          />
        </Grid>

        <Grid item xs={12}>
          <NewReqConfirmationMessage>
            {submittedRequisition.bill_to_insurance_type === "Attach" ||
            (submittedRequisition.is_fetal_antigen &&
              submittedRequisition.fetal_antigen_urls?.length === 0) ? (
              <>
                <NewReqConfirmationMessageText>
                  <span className="new-req-confirmation-message-insurance">
                    {GetTypographyContent({
                      content: "Please don't forget to  ",
                      size: "medium",
                      textColor: "blueDark",
                    })}
                    {GetTypographyContent({
                      content: "print",
                      size: "medium",
                      textColor: "blueDark",
                      isBold: true,
                    })}
                    {GetTypographyContent({
                      content: " and ",
                      size: "medium",
                      textColor: "blueDark",
                    })}
                    {GetTypographyContent({
                      content: "attach",
                      size: "medium",
                      textColor: "blueDark",
                      isBold: true,
                    })}
                    {GetTypographyContent({
                      content: " the following documents to the test kit.",
                      size: "medium",
                      textColor: "blueDark",
                    })}
                  </span>
                </NewReqConfirmationMessageText>
                <div>
                  <PrintIcon />
                </div>
                <div>
                  <NewReqBillingConfirmationList>
                    {submittedRequisition.is_fetal_antigen &&
                      submittedRequisition.fetal_antigen_urls?.length === 0 && (
                        <li>
                          {GetTypographyContent({
                            content:
                              "Lab report or consultation note to confirm the patient's alloimmunization",
                            size: "medium",
                            textColor: "blueDark",
                          })}
                        </li>
                      )}
                    {submittedRequisition.bill_to_insurance_type ===
                      "Attach" && (
                      <li>
                        {GetTypographyContent({
                          content: "Insurance information sheet",
                          size: "medium",
                          textColor: "blueDark",
                        })}
                      </li>
                    )}
                    <li>
                      {GetTypographyContent({
                        content: "Order confirmation page",
                        size: "medium",
                        textColor: "blueDark",
                      })}
                    </li>
                  </NewReqBillingConfirmationList>
                </div>{" "}
              </>
            ) : (
              <NewReqConfirmationMessageText>
                <span className="new-req-confirmation-message">
                  <PrintIcon />
                  {GetTypographyContent({
                    content: "Please don't forget to  ",
                    size: "medium",
                    textColor: "blueDark",
                  })}
                  {GetTypographyContent({
                    content: "print",
                    size: "medium",
                    textColor: "blueDark",
                    isBold: true,
                  })}
                  {GetTypographyContent({
                    content: " and ",
                    size: "medium",
                    textColor: "blueDark",
                  })}
                  {GetTypographyContent({
                    content: "attach",
                    size: "medium",
                    textColor: "blueDark",
                    isBold: true,
                  })}
                  {GetTypographyContent({
                    content: " order confirmation page to the test kit.",
                    size: "medium",
                    textColor: "blueDark",
                  })}
                </span>
              </NewReqConfirmationMessageText>
            )}
          </NewReqConfirmationMessage>
        </Grid>
        <Grid item xs={12}>
          <BoxConfirmation>
            <ConfirmationGraphic />
          </BoxConfirmation>
        </Grid>
      </Grid>
      <NewReqConfirmationButtonWrapper>
        <Box sx={{ display: "flex", gap: "15px" }}>
          <Button
            label="CLOSE"
            onClick={() => handleClose()}
            size="medium"
            reverse
          />
          <Button label="PRINT" onClick={() => handlePrint()} size="medium" />
        </Box>
      </NewReqConfirmationButtonWrapper>
    </ConfirmationWrapper>
  );
};
export default Confirmation;
